import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Link, useOutletContext } from "react-router-dom";
import toastr from "../../../utils/toast";

function BulkAddLinkedinUrl({ role_id, onClose }) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
  } = useForm({
    defaultValues: {
      linkedin_url: Array(10).fill({ url: "" }), // create 10 empty fields
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "linkedin_url",
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const REACT_APP_PINE_BASE_URL = process.env.REACT_APP_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const lastCheckedRef = useRef({});
  const [checkingIndex, setCheckingIndex] = useState({});
  const [image, setImage] = useState({});
  const [waitingList, setWaitingList] = useState([]);
  const [showQueueList, setSetshowQueueList] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const new_image = REACT_APP_PINE_BASE_URL + "/assets/img/icons/new.gif";
  const existing = REACT_APP_PINE_BASE_URL + "/assets/img/icons/success.png";
  const gdpr = REACT_APP_PINE_BASE_URL + "/assets/img/icons/gdpr-block.png";
  const loader =
    REACT_APP_PINE_BASE_URL + "/assets/img/icons/icon-loader-black.gif";

  const checkUrlExists = async (url, index) => {
    try {
      if (!url) {
        setImage({ ...image, [index]: null });
        return;
      }

      if (lastCheckedRef.current[index] === url) return;

      lastCheckedRef.current[index] = url;

      setActiveIndex(index);
      setCheckingIndex((prev) => ({ ...prev, [index]: true }));
      setImage({ ...image, [index]: null });

      const res = await axios.post(
        `${API_BASE_URL}/candidates/candidatecheckByLinkdin/`,
        {
          linkdin_url: url,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.length <= 0) {
        if (!errors.linkedin_url?.[index]?.url) {
          // clearErrors(`linkedin_url.${index}.url`);
          setImage({ ...image, [index]: new_image });
        }
      } else {
        if (res.data[0].gdpr === "1") setImage({ ...image, [index]: gdpr });
        else setImage({ ...image, [index]: existing });
        // setError(`linkedin_url.${index}.url`, {
        //   type: "manual",
        // });
        // console.log(res.data);
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Try Again.");
    } finally {
      setCheckingIndex((prev) => ({ ...prev, [index]: false }));
      setActiveIndex(null);
    }
  };

  // 1️⃣ Any form errors?
  const hasErrors = Object.keys(errors).length > 0;

  // 2️⃣ Any API still checking?
  const isChecking = Object.values(checkingIndex).some(Boolean);

  // 3️⃣ Any existing/manual errors specifically?
  const hasManualErrors = Object.values(errors?.linkedin_url || {}).some(
    (err) => err?.url?.type === "manual",
  );

  const get_waiting_list = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/candidates/get_waiting_list/${role_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setWaitingList(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    get_waiting_list();
  }, [role_id]);

  const add_linkedin_url = async (cleaned) => {
    try {
      setGlobalLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}/candidates/bulk_add_linkedin_url`,
        {
          role_id: role_id,
          urls: cleaned,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastr.success(res.data.message);
      console.log(res);
      onClose();
    } catch (err) {
      console.error(err);
      toastr.error(err.response.data.message);
      setGlobalLoading(false);
    } finally {
      setGlobalLoading(false);
    }
  };

  const onSubmit = (data) => {
    console.log(hasErrors || hasManualErrors || isChecking);
    if (hasErrors || hasManualErrors || isChecking) {
      return; // block submit
    }
    const cleaned = data.linkedin_url
      .map((item) => normalizeLinkedInUrl(item.url))
      .filter((url) => url !== "");

    if (cleaned.length === 0) {
      alert("Please enter at least 1 valid LinkedIn URL.");
      return;
    }

    add_linkedin_url(cleaned);
  };

  const normalizeLinkedInUrl = (input) => {
    if (!input) return "";

    let url = input.trim();

    // If only username is entered
    if (!url.includes("linkedin.com")) {
      return `https://www.linkedin.com/in/${url.replace(/^\/+|\/+$/g, "")}/`;
    }

    // Ensure protocol
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }

    try {
      const parsed = new URL(url);

      // Remove subdomains like in.linkedin.com, uk.linkedin.com
      parsed.hostname = "www.linkedin.com";

      // Remove query params and hash
      parsed.search = "";
      parsed.hash = "";

      const pathParts = parsed.pathname.split("/").filter(Boolean);

      if (pathParts[0] === "in" && pathParts[1]) {
        return `https://www.linkedin.com/in/${pathParts[1]}/`;
      }

      return "";
    } catch {
      return "";
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Linkedin Url</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => onClose()}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit(onSubmit)}>
                {fields.map((field, index) => (
                  <div className="mb-1" key={field.id}>
                    <label className="form-label">
                      {index === 0 && showQueueList === false
                        ? "Linkedin URL"
                        : index === 0 && showQueueList === true
                          ? "Waiting List"
                          : ""}
                    </label>
                    {waitingList?.length > 0 && index === 0 && (
                      <>
                        {showQueueList === false && (
                          <span className="avatar avatar-sm bg-primary avatar-rounded text-fixed-white fs-10 float-end">
                            {waitingList?.length}
                          </span>
                        )}
                        <Link
                          onClick={() => {
                            showQueueList === true
                              ? setSetshowQueueList(false)
                              : setSetshowQueueList(true);
                          }}
                          className="btn btn-sm btn-primary float-end me-1"
                        >
                          {showQueueList === true ? "Add Form" : "Queue List"}
                        </Link>
                      </>
                    )}
                    {showQueueList === true ? (
                      index === 0 && (
                        <>
                          <ul className="list-group list-group-flush list-decimal">
                            {waitingList?.map((item, index) => (
                              <li>
                                <a target="_blank" href={item.linkdin_url}>
                                  {item.linkdin_url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </>
                      )
                    ) : (
                      <>
                        <input
                          key={field.id}
                          type="url"
                          disabled={isChecking && activeIndex !== index}
                          className={`form-control  ${
                            errors.linkedin_url?.[index]?.url
                              ? errors.linkedin_url?.[index]?.url.type ===
                                "manual"
                                ? "border border-danger"
                                : "is-invalid"
                              : null
                          }`}
                          {...register(`linkedin_url.${index}.url`, {
                            validate: (value) => {
                              if (!value) return true; // allow empty
                              // const pattern =
                              //   /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/i;
                              const pattern =
                                /^(https?:\/\/)?([\w]+\.)?linkedin\.com\/.*$/i;

                              return (
                                pattern.test(value) ||
                                "Enter a valid LinkedIn URL"
                              );
                            },
                          })}
                          onBlur={(e) => {
                            const rawValue = e.target.value.trim();

                            if (!rawValue) {
                              setImage({ ...image, [index]: null });
                              return;
                            }

                            const normalized = normalizeLinkedInUrl(rawValue);

                            if (!normalized) {
                              return;
                            }

                            e.target.value = normalized; // overwrite input with clean URL
                            checkUrlExists(normalized, index);
                          }}
                          onPaste={(e) => {
                            setTimeout(() => {
                              const rawValue = e.target.value.trim();

                              if (!rawValue) {
                                setImage({ ...image, [index]: null });
                                return;
                              }

                              const normalized = normalizeLinkedInUrl(rawValue);

                              if (!normalized) return;

                              e.target.value = normalized;
                              checkUrlExists(normalized, index);
                            }, 10);
                          }}
                        />
                        {image[index] && (
                          <span className="position-absolute end-0 translate-middle-y me-4">
                            <img
                              className="mb-4"
                              src={image[index]}
                              alt="loader"
                            />
                          </span>
                        )}
                        {checkingIndex[index] && (
                          <span className="position-absolute end-0 translate-middle-y me-4">
                            <img className="mb-4" src={loader} alt="loader" />
                          </span>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {showQueueList === false && (
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={hasErrors || hasManualErrors || isChecking}
                    >
                      Save changes
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BulkAddLinkedinUrl;
